// lib/pdf/facing-text-layout.js
// Facing-text layout for picture books read by ages 8-13 (reader bands O and W) drawn by the comic engine:
// each story page becomes TWO pages, a text page (cream, centred narration) then a full-page art page
// (square art, contain on white, no crop). Cover, dedication and back cover are unchanged.
//
// Plain CommonJS with no TypeScript imports: the PDF worker (worker/robustPdfGeneratorWorker.js) loads
// lib/pdf/generator.js directly. The TypeScript print worker imports these helpers from here too, so the
// digital and print layouts share one definition.
//
// The look follows the hardcover text pages (services/print/hardcover/html.ts buildInnerTextPagesHtml):
// cream #fbf7ef page, #2b2420 text, line-height 1.5, centred, 22pt shrinking to an 11pt floor, 15mm margin
// inside a 200mm trim. Sizes are scaled by page width (see facingTextFontRange / facingTextBox); ruling R16
// ratified the scaled range (22pt on a 1024pt page would read as tiny).

const PICTURE_BOOK_PRODUCT_TYPE = 'picture-book';
const FACING_TEXT_BANDS = Object.freeze(['O', 'W']);

/** Hardcover trim width in points (200mm), the page the 22pt/11pt sizes were chosen for. */
const HARDCOVER_TRIM_PT = (200 / 25.4) * 72; // 566.93
/** Hardcover story text: start size and floor, in points on the 200mm trim. */
const HARDCOVER_STORY_PT = Object.freeze({ max: 22, min: 11 });
/** Hardcover text margin: 15mm inside the 200mm trim, as a share of the page width. */
const FACING_TEXT_MARGIN_RATIO = 15 / 200; // 0.075

const FACING_TEXT_STYLE = Object.freeze({
  background: '#fbf7ef', // text page (hardcover .page)
  color: '#2b2420',      // text colour (hardcover body)
  lineHeight: 1.5,       // hardcover .story
  artBackground: '#ffffff', // art page behind the contained art
  fontStep: 0.5,         // shrink step, as the hardcover FIT_SCRIPT
});

function parseMetadata(metadata) {
  if (metadata && typeof metadata === 'object') return metadata;
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

/**
 * The metadata object carrying the comic-engine stamp: book.metadata, or book.book_metadata (ruling R15: the PDF
 * worker replaces book.metadata with metadata parsed from additional_comments and keeps the books.metadata column
 * as book_metadata). Either may be an object or a JSON string. Null when neither has engine === 'comic'.
 */
const { bodyFontFor, scaledPt } = require('./font-stack');

function stampedMetadata(book) {
  if (!book) return null;
  for (const raw of [book.metadata, book.book_metadata]) {
    const meta = parseMetadata(raw);
    if (meta && meta.engine === 'comic') return meta;
  }
  return null;
}

/** Same check as lib/books/product-type.ts isComicEnginePictureBook (duplicated: this file cannot import TS). The band rule below is the single source: product-type.ts re-exports it. */
function isComicEnginePictureBookData(book) {
  if (!book) return false;
  const isPicture = book.product_type === PICTURE_BOOK_PRODUCT_TYPE || book.productType === PICTURE_BOOK_PRODUCT_TYPE;
  return isPicture && !!stampedMetadata(book);
}

/** Source of the O/W band rule; lib/books/product-type.ts re-exports this function. */
function isFacingTextBand(band) {
  return FACING_TEXT_BANDS.includes(band);
}

/**
 * True when the book's story pages use the facing-text layout:
 * product_type (or productType) 'picture-book' && metadata.engine === 'comic' && metadata.picture_book.band in O/W,
 * the stamp read from book.metadata or book.book_metadata (R15).
 * @param {object|null|undefined} book a books row (metadata / book_metadata may be an object or a JSON string)
 * @returns {boolean}
 */
function usesFacingText(book) {
  if (!isComicEnginePictureBookData(book)) return false;
  const meta = stampedMetadata(book);
  return isFacingTextBand(meta && meta.picture_book ? meta.picture_book.band : undefined);
}

/**
 * Ruling R16. Narration font size range for a text page of the given width, in the same unit as the width:
 * the hardcover's 22pt start / 11pt floor scaled by pageWidth / 200mm, rounded to whole units.
 * 1024-wide digital PDFKit page: { max: 40, min: 20 }; 768pt Playwright page: { max: 30, min: 15 };
 * a 200mm (566.93pt) print trim: { max: 22, min: 11 }.
 * @param {number} pageWidth
 * @returns {{ max: number, min: number }}
 */
function facingTextFontRange(pageWidth, band) {
  const s = pageWidth / HARDCOVER_TRIM_PT;
  const min = Math.round(HARDCOVER_STORY_PT.min * s);
  const bandSize = band ? bodyFontFor(band).sizePt : null;
  if (bandSize) return { max: Math.max(scaledPt(bandSize, pageWidth), min), min };
  return { max: Math.round(HARDCOVER_STORY_PT.max * s), min };
}

const READER_BANDS = ['T', 'E', 'M', 'O', 'W', 'G'];

/** The reading band stamped on a comic-engine picture book (metadata or book_metadata), or null. Never throws. */
function readingBandOf(book) {
  try {
    const band = stampedMetadata(book)?.picture_book?.band;
    return READER_BANDS.includes(band) ? band : null;
  } catch {
    return null;
  }
}

/**
 * The box the narration is fitted into on a text page: the page inset by 7.5% of its width on every side
 * (the hardcover's 15mm inside a 200mm trim). Text is centred horizontally and vertically in it.
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
function facingTextBox(pageWidth, pageHeight) {
  const m = pageWidth * FACING_TEXT_MARGIN_RATIO;
  return { x: m, y: m, width: pageWidth - 2 * m, height: pageHeight - 2 * m };
}

/**
 * "contain" placement: the largest box with the image's aspect ratio that fits the page, centred. No crop.
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
function containBox(pageWidth, pageHeight, imageWidth, imageHeight) {
  const scale = Math.min(pageWidth / imageWidth, pageHeight / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return { x: (pageWidth - width) / 2, y: (pageHeight - height) / 2, width, height };
}

/**
 * Largest font size in { max .. min } (0.5 steps) at which measureHeight(size) <= maxHeight; min if none fits.
 * @param {(size: number) => number} measureHeight height of the laid-out text at a font size
 * @param {number} maxHeight
 * @param {{ max: number, min: number }} range
 * @returns {number}
 */
function fitFontSize(measureHeight, maxHeight, range) {
  let size = range.max;
  while (size > range.min && measureHeight(size) > maxHeight) {
    size = Math.max(range.min, size - FACING_TEXT_STYLE.fontStep);
  }
  return size;
}

/**
 * Expand an HTML-template page list: each story page ({ pageNumber, text, backgroundImage }) becomes
 * { kind: 'facing-text', text, pageNumber } then { kind: 'facing-art', backgroundImage, pageNumber }.
 * Pages with a kind (dedication...) are left as they are.
 */
function expandFacingTextPages(pages) {
  const out = [];
  for (const page of pages || []) {
    if (page && page.kind) { out.push(page); continue; }
    out.push({ kind: 'facing-text', text: page.text || '', pageNumber: page.pageNumber });
    out.push({ kind: 'facing-art', backgroundImage: page.backgroundImage, pageNumber: page.pageNumber });
  }
  return out;
}

module.exports = {
  PICTURE_BOOK_PRODUCT_TYPE,
  FACING_TEXT_BANDS,
  FACING_TEXT_STYLE,
  HARDCOVER_STORY_PT,
  HARDCOVER_TRIM_PT,
  FACING_TEXT_MARGIN_RATIO,
  isComicEnginePictureBookData,
  isFacingTextBand,
  usesFacingText,
  facingTextFontRange,
  readingBandOf,
  facingTextBox,
  containBox,
  fitFontSize,
  expandFacingTextPages,
};
